import React, { useEffect, useState } from "react";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { apiService } from "src/services/api.service";
import { ICourseSessionRegistration, ICourse } from "@eagerdog/interfaces";

import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import { download } from "src/components/Download/DownloadManager";

import { type ColDef, GridApi, type TextFilterModel, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { AttendanceRenderer } from "./AttendanceRenderer";
import { ActionsRenderer } from "./ActionsRenderer";

import styles from "./CourseRegistrations.module.scss";

interface IProps {
    registrations: ICourseSessionRegistration[],
    course: ICourse,
    sessionFilter: number | undefined
}

interface IRow {
    call_name: string | undefined,
    attendance: any,
    session: string,
    breed: string,
    handler_name: string,
    handler_email: string,
    registered_at: string
}

interface IFilters {
  session?: TextFilterModel
}

const CourseRegistrations: React.FC<IProps> = (props) => {
    const { course } = props;
    const [rowData, setRowData] = useState<IRow[]>([]);
    const [gridApi, setGridApi] = useState<GridApi<IRow>>();

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    }

    const [registrations, setRegistrations] = useState<ICourseSessionRegistration[]>(props.registrations);

    const theme = themeQuartz.withParams({
        fontFamily: "Roboto",
        headerFontFamily: "Roboto",
        cellFontFamily: "Roboto"
    });

    const isSeminar = course.course_type === Constants.course_type.seminar;

    const handlerNameCol: ColDef<IRow> = {
        field: "handler_name",
        headerName: "Handler's name",
        minWidth: 140,
        filter: "agTextColumnFilter",
    };
    const handlerEmailCol: ColDef<IRow> = {
        field: "handler_email",
        headerName: "Handler's email",
        minWidth: 180,
        filter: "agTextColumnFilter",
    };

    let colDefs: ColDef<IRow>[] = [
        {
            field: "call_name",
            headerName: isSeminar ? "Name" : "Call Name",
            minWidth: 120,
            filter: "agTextColumnFilter",
        },
        { field: "session", minWidth: 120, cellDataType: "text", filter: "agTextColumnFilter" },
        {
            headerName: "Attendance",
            field: "attendance",
            cellRenderer: AttendanceRenderer,
            cellRendererParams: {
                onUpdateAttendance: (status: string, id: string) => {
                    apiService.updateCourseSessionRegistrationStatus(course._id, id, { status: status }).then((response) => {
                        setRegistrations(registrations.map((value: ICourseSessionRegistration) => {
                            return value._id === response._id ? response : value;
                        }));
                    }).catch((error: any) => {
                        toast.show({
                            title: "Update Attendance",
                            content: error.response.data.message ? error.response.data.message : "Something went wrong, please try again later",
                            duration: 10000,
                            type: "fail"
                        });
                    });
                }
            },
            resizable: false,
            cellStyle: {
                "overflow": "visible",
                "zIndex": "2"
            }
        },
    ];

    if (!isSeminar) {
        colDefs.push({ field: "breed", minWidth: 120, filter: true });
    }

    colDefs.push(handlerNameCol, handlerEmailCol, {
        field: "registered_at",
        headerName: "Registered",
        minWidth: 120,
    });

    const onRemoveRegistration = (registrationId: string) => {
        setRegistrations((prev) => prev.filter((reg) => reg._id !== registrationId));
    };

    colDefs.push({
        headerName: "",
        colId: "actions",
        cellRenderer: ActionsRenderer,
        cellRendererParams: {
            course: course,
            onRemoved: onRemoveRegistration,
        },
        sortable: false,
        filter: false,
        resizable: false,
        maxWidth: 64,
        minWidth: 64,
        cellStyle: {
            "overflow": "visible",
            "zIndex": "1"
        }
    });
    
    useEffect(() => {
        setRegistrations(props.registrations);
    }, [props.registrations]);

    useEffect(() => {
        if (gridApi && props.sessionFilter !== undefined) {
            let filters: IFilters = {};

            filters.session = {
                filterType: "text",
                type: "contains",
                filter: "Session " + (props.sessionFilter),
            }

            gridApi.setFilterModel(filters);
        }
    }, [gridApi, props.sessionFilter]);

    useEffect(() => {
        let rows: IRow[] = [];

        const getSessionIndex = (id: string) => {
            return course.schedule.findIndex((c) => c._id === id);
        }

        if (registrations) {
            for (let r in registrations) {
                let reg = registrations[r];

                rows.push({
                    call_name: isSeminar
                        ? `${reg.owner_name}${reg.owner_email ? ` (${reg.owner_email})` : ''}` // For seminars, show name and email
                        : reg.call_name + " (" + reg.registered_name + ")", // For courses, show dog name
                    session: "Session " + (getSessionIndex(reg.session_id) + 1),
                    attendance: {
                        registration: reg
                    },
                    breed: reg.breed || "", // Empty for seminars
                    handler_name: reg.owner_name || "",
                    handler_email: reg.owner_email || "",
                    registered_at: moment(reg.created_at).format("MMMM do, yy, hh:mmA")
                });
            }

            setRowData(rows);
        }
    }, [registrations, isSeminar]);

    const onExportRoster = () => {
        download.pushDownload({
            type: "course_roster",
            courseId: course._id,
        });
    };

    return (
        <div className={styles.CourseRegistrations}>
            <div className={styles.toolbar}>
                <Button onClick={onExportRoster}>
                    Export
                </Button>
            </div>
            <AgGridReact
                onGridReady={onGridReady}
                rowData={rowData}
                columnDefs={colDefs}
                domLayout="normal"
                gridOptions={{
                    defaultColDef: {
                        flex: 1,
                        minWidth: 100,
                        resizable: true
                    },
                    animateRows: true,
                    rowHeight: 48,
                    headerHeight: 48,
                    overlayNoRowsTemplate: '<span>No Registrations</span>'
                }}
            />
        </div>
    );
};

export default CourseRegistrations;
